import { useMemo, useState } from "react";

import { GridRowSelectionModel } from "@mui/x-data-grid";
import { emptyRowSelection, useFormikWithoutDirtyForm } from "@shared";
import { useQueryClient } from "@tanstack/react-query";
import { startOfYesterday } from "date-fns";

import { EdictEntryResponse, EdictSelectionRequest, EdictSource } from "@/api";
import { useTranslation } from "@/translations";

import { EdictFilterValues, EdictRow, EdictSearchParams } from "../Edicts.types";
import { isFileableEdict, isOzufLetterRecipient } from "../Edicts.utils";
import { edictsFilterValidationSchema } from "../components/filter/EdictsFilterBar.utils";
import { useCreateEdictRecords } from "./useCreateEdictRecords";
import { edictsQueryKey, useEdictsQuery } from "./useEdictsQuery";
import { useOzufMailMerge } from "./useOzufMailMerge";

export function useEdicts() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Kept per register so switching the source shows that register's own last search run instead of an empty
  // table, without ever mixing the two.
  const [searchParamsBySource, setSearchParamsBySource] = useState<Partial<Record<EdictSource, EdictSearchParams>>>({});
  // The default covers exactly the day the registers were last published for, so opening the page never
  // queries more than one day by accident. Both fields are independent afterwards — a longer period is a
  // deliberate override, e.g. when insolvency edicts (Edikte) were not imported over the holidays.
  const [defaultDate] = useState(startOfYesterday);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(emptyRowSelection);
  const [onlyWithoutCounselor, setOnlyWithoutCounselor] = useState(false);
  // The clicked entry itself, not its row position: the overlay then survives anything that renumbers
  // the rows behind it, and closing it leaves the table exactly as it was.
  const [detailEntry, setDetailEntry] = useState<EdictRow | null>(null);

  const clearSelection = () => setSelectionModel(emptyRowSelection());

  const formik = useFormikWithoutDirtyForm<EdictFilterValues>({
    initialValues: { source: EdictSource.Edikt, from: defaultDate, to: defaultDate },
    validationSchema: edictsFilterValidationSchema(t),
    // Validate on "Daten laden" only: typing a period is not an error until it is used, and the register
    // rules differ per source. Editing a field therefore leaves the last verdict standing until the next
    // run — switching the source revalidates explicitly, because the source decides which rules apply.
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (values.from === null || values.to === null) {
        return;
      }
      const params: EdictSearchParams = { source: values.source, from: values.from, to: values.to };

      // Only this register and period are invalidated, so starting a search run does not discard the other
      // register's result. Re-running an unchanged period has to refetch, which a cache hit would not.
      void queryClient.invalidateQueries({ queryKey: edictsQueryKey(params) });
      setSearchParamsBySource((current) => ({ ...current, [params.source]: params }));
      clearSelection();
    },
  });

  const searchParams = searchParamsBySource[formik.values.source] ?? null;
  // Scoped to the selected register on purpose: the other register stays startable while a long run walks
  // its pages, and re-running the selected one cannot double up because the query key dedups.
  const { data, isFetching, isError } = useEdictsQuery(searchParams);
  const entries = useMemo(() => data ?? [], [data]);

  // The filter lets the person on edict duty isolate their own share of the result set: the clients
  // without a fixed counselor, whose assignments would otherwise land on them anyway. An unmatched row has
  // no counselor either, but it has no client file to be without one — it is not part of that share.
  const visibleEntries = useMemo(
    () =>
      onlyWithoutCounselor
        ? entries.filter((entry) => entry.clientId !== null && entry.counselorUserCode === null)
        : entries,
    [entries, onlyWithoutCounselor]
  );

  const createRecords = useCreateEdictRecords(searchParams, clearSelection);
  const mailMerge = useOzufMailMerge(clearSelection);

  // The result belongs to the source it was fetched for, so switching back to a register restores its own
  // last search run rather than clearing the table. Its period comes back with it, so the filter bar keeps
  // describing what the table shows.
  const handleSourceChange = (next: EdictSource) => {
    const previous = searchParamsBySource[next];

    clearSelection();

    // Revalidated because the maximum-period rule applies to Edikte only: a verdict reached for one
    // register would otherwise stay on screen under the other, where it does not hold.
    void formik.setValues(
      {
        source: next,
        from: previous?.from ?? formik.values.from,
        to: previous?.to ?? formik.values.to,
      },
      true
    );
  };

  const handleOnlyWithoutCounselorChange = (next: boolean) => {
    // The rows are identified by their position in the visible list, so a changed list invalidates the
    // selection — and the header checkbox is meant to tick exactly what is on screen.
    clearSelection();
    setOnlyWithoutCounselor(next);
  };

  // Each register selects rows for its own action: an edict is filed into a client file, an OZUF entry
  // gets the letter to someone who is not a client yet.
  const isOzufs = formik.values.source === EdictSource.Ozufs;
  const isEntrySelectable = isOzufs ? isOzufLetterRecipient : isFileableEdict;

  const selectedEntries = useMemo<EdictEntryResponse[]>(() => {
    // "Select all" arrives as type "exclude" with an empty id set, i.e. everything but nothing —
    // reading the ids as the selection would report no rows selected while all render ticked. It also
    // covers rows the table renders without a checkbox, so those are dropped here rather than counted.
    const { type, ids } = selectionModel;

    return visibleEntries.flatMap((entry, index) => {
      const isTicked = type === "include" ? ids.has(index.toString()) : !ids.has(index.toString());

      return isTicked && isEntrySelectable(entry) ? [entry] : [];
    });
  }, [visibleEntries, selectionModel, isEntrySelectable]);

  const fileableEntries = useMemo<EdictSelectionRequest[]>(
    () =>
      selectedEntries.filter(isFileableEdict).map(({ clientId, sourceReference, fileNumber }) => ({
        sourceReference,
        fileNumber,
        clientId,
      })),
    [selectedEntries]
  );

  const handleCreateRecords = () => {
    if (fileableEntries.length > 0) {
      createRecords.mutate(fileableEntries);
    }
  };

  const handleCreateMailMerge = () => void mailMerge.createMailMerge(selectedEntries);

  // Filing exists for Edikte only: the OZUF counterpart was not carried over from the legacy.
  const showCreateRecordsButton = !isOzufs;

  return {
    formik,
    entries: visibleEntries,
    isFetching,
    isError,
    handleSourceChange,
    // The query key already identifies the result set (register plus period), so it doubles as the
    // signature that sends the table back to page 1 whenever a new search run is shown.
    paginationResetKey: edictsQueryKey(searchParams).join("|"),
    hasSearched: searchParams !== null,
    // An empty table after filtering means something different from a period that returned nothing, and
    // the two must not share a message.
    isFilteredEmpty: visibleEntries.length === 0 && entries.length > 0,
    showMailMergeButton: isOzufs,
    // Only the OZUF listing states where the debtor lives, and there the city decides who can receive the
    // letter — the column is what explains a non-client row without a checkbox.
    showCityColumn: isOzufs,
    onlyWithoutCounselor,
    handleOnlyWithoutCounselorChange,
    selectionModel,
    setSelectionModel,
    isEntrySelectable,
    selectedCount: selectedEntries.length,
    showCreateRecordsButton,
    handleCreateRecords,
    isCreatingRecords: createRecords.isPending,
    handleCreateMailMerge,
    isWritingMailMerge: mailMerge.isWriting,
    mailMergeFolderDialog: mailMerge.folderDialog,
    detailEntry,
    setDetailEntry,
  };
}
